<template>
  <div class="agent-corp-mail-manager">
    <div class="d-flex justify-content-end align-items-center mb-4">
      <b-button 
        variant="primary" 
        @click="showCreateModal = true"
      >
        <i class="fas fa-plus mr-1"></i>
        Добавить корпоративную почту
      </b-button>
    </div>

    <!-- Таблица корпоративных почт -->
    <b-card>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>ID</th>
              <th>Агент</th>
              <th>Email агента</th>
              <th>Корпоративная почта</th>
              <th>Статус</th>
              <th>Создана</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center">
                <i class="fas fa-spinner fa-spin"></i> Загрузка...
              </td>
            </tr>
            <tr v-else-if="corpMails.length === 0">
              <td colspan="7" class="text-center text-muted">
                Корпоративные почты не найдены
              </td>
            </tr>
            <tr v-for="corpMail in corpMails" :key="corpMail.id">
              <td>{{ corpMail.id }}</td>
              <td>
                <strong>{{ corpMail.user_name }}</strong>
              </td>
              <td>{{ corpMail.user_email }}</td>
              <td>
                <code>{{ corpMail.corp_email }}</code>
              </td>
              <td>
                <b-badge :variant="corpMail.is_active ? 'success' : 'danger'">
                  {{ corpMail.is_active ? 'Активна' : 'Неактивна' }}
                </b-badge>
              </td>
              <td>{{ formatDate(corpMail.created_at) }}</td>
              <td>
                <b-button-group size="sm">
                  <b-button 
                    variant="secondary" 
                    @click="viewCorpMail(corpMail)"
                    title="Просмотр"
                  >
                    <i class="fas fa-eye"></i>
                  </b-button>
                  <b-button 
                    variant="secondary" 
                    @click="editCorpMail(corpMail)"
                    title="Редактировать"
                  >
                    <i class="fas fa-edit"></i>
                  </b-button>
                  <b-button 
                    variant="info" 
                    @click="copyPassword(corpMail.id)"
                    title="Копировать пароль"
                  >
                    <i class="fas fa-copy"></i>
                  </b-button>
                  <b-button 
                    variant="danger" 
                    @click="deleteCorpMail(corpMail)"
                    title="Удалить"
                  >
                    <i class="fas fa-trash"></i>
                  </b-button>
                </b-button-group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <!-- Модальное окно создания/редактирования -->
    <b-modal
      :id="'corp-mail-modal'"
      :title="isEditing ? 'Редактировать корпоративную почту' : 'Добавить корпоративную почту'"
      size="lg"
      v-model="showCreateModal"
      @hidden="resetForm"
    >
      <b-form @submit.prevent="submitForm">
        <b-row>
          <b-col cols="12">
            <b-form-group label="Агент *" label-for="user-select">
              <SelectAgentWithCorpMail
                id="user-select"
                :selected="form.user_id"
                :disabled="isEditing"
                @set="form.user_id = $event"
              />
            </b-form-group>
          </b-col>
          
          <b-col cols="12">
            <b-form-group label="Корпоративная почта *" label-for="email">
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                required
                placeholder="agent@company.com"
              ></b-form-input>
            </b-form-group>
          </b-col>
          
          <b-col cols="12">
            <b-form-group label="Пароль *" label-for="password">
              <b-form-input
                id="password"
                v-model="form.password"
                type="password"
                required
                placeholder="Введите пароль"
              ></b-form-input>
            </b-form-group>
          </b-col>
          
          <b-col cols="12">
            <b-form-group>
              <b-form-checkbox v-model="form.is_active">
                Активна
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      
      <template #modal-footer>
        <b-button variant="secondary" @click="showCreateModal = false">
          Отмена
        </b-button>
        <b-button 
          variant="primary" 
          @click="submitForm"
          :disabled="submitting"
        >
          <i class="fas fa-spinner fa-spin mr-1" v-if="submitting"></i>
          {{ isEditing ? 'Сохранить' : 'Создать' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Модальное окно просмотра -->
    <b-modal
      :id="'view-corp-mail-modal'"
      title="Данные корпоративной почты"
      size="lg"
      v-model="showViewModal"
    >
      <div v-if="viewingCorpMail">
        <b-row>
          <b-col cols="6">
            <strong>Агент:</strong>
            <p>{{ viewingCorpMail.user_name }}</p>
          </b-col>
          <b-col cols="6">
            <strong>Email агента:</strong>
            <p>{{ viewingCorpMail.user_email }}</p>
          </b-col>
          <b-col cols="6">
            <strong>Корпоративная почта:</strong>
            <p><code>{{ viewingCorpMail.corp_email }}</code></p>
          </b-col>
          <b-col cols="6">
            <strong>Пароль:</strong>
            <p>
              <code>{{ passwordVisible ? (viewingCorpMail.password || passwordCache[viewingCorpMail.id] || '') : '••••••••' }}</code>
              <b-button 
                size="sm" 
                variant="outline-secondary" 
                @click="togglePasswordVisibility"
                class="ml-2"
                title="Показать/скрыть пароль"
              >
                <i :class="passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </b-button>
              <b-button 
                size="sm" 
                variant="outline-primary" 
                @click="copyPassword(viewingCorpMail.id, viewingCorpMail.password || passwordCache[viewingCorpMail.id])"
                class="ml-2"
                title="Копировать пароль"
              >
                <i class="fas fa-copy"></i> Копировать
              </b-button>
            </p>
          </b-col>
          <b-col cols="6">
            <strong>Статус:</strong>
            <p>
              <b-badge :variant="viewingCorpMail.is_active ? 'success' : 'danger'">
                {{ viewingCorpMail.is_active ? 'Активна' : 'Неактивна' }}
              </b-badge>
            </p>
          </b-col>
          <b-col cols="6">
            <strong>Создана:</strong>
            <p>{{ formatDate(viewingCorpMail.created_at) }}</p>
          </b-col>
        </b-row>
      </div>
      
      <template #modal-footer>
        <b-button variant="secondary" @click="showViewModal = false">
          Закрыть
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import { API_AGENT_CORP_MAIL } from "@/constants"
  import SelectAgentWithCorpMail from './SelectAgentWithCorpMail.vue'

  export default {
    name: 'AgentCorpMailManager',
    components: {
      SelectAgentWithCorpMail
    },
    data() {
      return {
        corpMails: [],
        loading: false,
        submitting: false,
        showCreateModal: false,
        showViewModal: false,
        isEditing: false,
        editingId: null,
        viewingCorpMail: null,
        passwordVisible: false,
        passwordCache: {}, // Кэш паролей для быстрого доступа
        form: {
          user_id: '',
          email: '',
          password: '',
          is_active: true
        }
      };
    },
    mounted() {
      this.loadCorpMails();
    },
    methods: {
      async loadCorpMails() {
        this.loading = true;
        try {
          const response = await api.call('get', API_AGENT_CORP_MAIL);
          this.corpMails = response.data.data;
        } catch (error) {
          console.error('Ошибка загрузки корпоративных почт:', error);
          this.$toast.error('Не удалось загрузить список корпоративных почт');
        } finally {
          this.loading = false;
        }
      },
      
      async submitForm() {
        this.submitting = true;
        try {
          if (this.isEditing) {
            await api.call('put', `${API_AGENT_CORP_MAIL}/${this.editingId}`, this.form);
            this.$toast.success('Корпоративная почта обновлена');
          } else {
            await api.call('post', API_AGENT_CORP_MAIL, this.form);
            this.$toast.success('Корпоративная почта создана');
          }
          
          this.showCreateModal = false;
          this.loadCorpMails();
          this.resetForm();
        } catch (error) {
          console.error('Ошибка сохранения:', error);
          if (error.response && error.response.data.errors) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach(key => {
              this.$toast.error(`${key}: ${errors[key][0]}`);
            });
          } else {
            this.$toast.error('Не удалось сохранить корпоративную почту');
          }
        } finally {
          this.submitting = false;
        }
      },
      
      async viewCorpMail(corpMail) {
        this.viewingCorpMail = corpMail;
        this.passwordVisible = false;
        // Загружаем полные данные с паролем
        try {
          const response = await api.call('get', `${API_AGENT_CORP_MAIL}/${corpMail.id}`);
          this.viewingCorpMail = response.data.data;
          // Кэшируем пароль
          this.passwordCache[corpMail.id] = response.data.data.password;
        } catch (error) {
          console.error('Ошибка загрузки данных:', error);
          this.$toast.error('Не удалось загрузить данные почты');
        }
        this.showViewModal = true;
      },
      
      togglePasswordVisibility() {
        this.passwordVisible = !this.passwordVisible;
      },
      
      editCorpMail(corpMail) {
        this.isEditing = true;
        this.editingId = corpMail.id;
        this.form = {
          user_id: corpMail.user_id,
          email: corpMail.corp_email,
          password: '', // Не загружаем пароль для безопасности
          is_active: corpMail.is_active
        };
        this.showCreateModal = true;
      },
      
      async deleteCorpMail(corpMail) {
        if (confirm(`Вы уверены, что хотите удалить корпоративную почту для ${corpMail.user_name}?`)) {
          try {
            await api.call('delete', `${API_AGENT_CORP_MAIL}/${corpMail.id}`);
            this.$toast.success('Корпоративная почта удалена');
            this.loadCorpMails();
          } catch (error) {
            console.error('Ошибка удаления:', error);
            this.$toast.error('Не удалось удалить корпоративную почту');
          }
        }
      },
      
      resetForm() {
        this.isEditing = false;
        this.editingId = null;
        this.passwordVisible = false;
        this.form = {
          user_id: '',
          email: '',
          password: '',
          is_active: true
        };
      },
      
      async copyPassword(corpMailId, password = null) {
        let passwordToCopy = password;
        
        // Если пароль не передан, загружаем его
        if (!passwordToCopy) {
          // Проверяем кэш
          if (this.passwordCache[corpMailId]) {
            passwordToCopy = this.passwordCache[corpMailId];
          } else {
            // Загружаем пароль с сервера
            try {
              const response = await api.call('get', `${API_AGENT_CORP_MAIL}/${corpMailId}`);
              passwordToCopy = response.data.data.password;
              this.passwordCache[corpMailId] = passwordToCopy;
            } catch (error) {
              console.error('Ошибка загрузки пароля:', error);
              this.$toast.error('Не удалось загрузить пароль');
              return;
            }
          }
        }
        
        try {
          await navigator.clipboard.writeText(passwordToCopy);
          this.$toast.success('Пароль скопирован');
        } catch (error) {
          // Fallback для старых браузеров
          const textArea = document.createElement('textarea');
          textArea.value = passwordToCopy;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          this.$toast.success('Пароль скопирован');
        }
      },
      
      formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
  };
</script>

<style scoped>
  .table th {
    border-top: none;
    font-weight: 600;
    color: #495057;
  }

  .table td {
    vertical-align: middle;
  }

  .table code {
    background: #f8f9fa;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.9em;
  }

  .btn-group .btn {
    margin-right: 2px;
  }

  .btn-group .btn:last-child {
    margin-right: 0;
  }

  /* Адаптивность */
  @media (max-width: 768px) {
    .agent-corp-mail-manager {
      padding: 10px;
    }
    
    .table-responsive {
      font-size: 0.9em;
    }
    
    .btn-group {
      display: flex;
      flex-direction: column;
    }
    
    .btn-group .btn {
      margin-bottom: 2px;
      margin-right: 0;
    }
  }
</style>
